/**
 * 地址里的邮件链接令牌。与 `takeRoomCode()` 并排，同一个「读一次就抹掉」的模式。
 *
 * ★ 必须在 AppRoot 的**首次渲染体内**同步读走，不能放进 useEffect：
 *   `startOnlineSession()` 的 boot 里会调 `startRoomRouteSync()`，它无条件
 *   `replaceState(null, '', '/')`，把 query 和 hash 一起抹掉。effect 跑的时候令牌已经没了。
 *
 * ★ 令牌走 **fragment**（`/reset#t=…`）而不是 query：fragment 永远不会出现在
 *   HTTP 请求行里，所以不进反代的 access log、不进 Referer、不进任何中间层。
 *   query 形式这三样全占。
 *
 * ★ 路径固定为 `/reset` 与 `/signin`，**不是** `/reset/<token>`：
 *   `parseInviteTarget` 会把路径末段拿去 `normalizeRoomCode` 当房间码。
 *   'RESET'/'SIGNIN' 长度不是 6，正好落不进去；但路径式令牌等于在赌末段长度，不值得赌。
 */
import { clearRoomRoute, type HistoryLike } from './route.js'

export type EmailLinkPurpose = 'reset' | 'login'

export interface EmailLinkTicket {
  readonly purpose: EmailLinkPurpose
  readonly token: string
}

function currentHref(): string {
  return (globalThis as { location?: { href?: string } }).location?.href ?? ''
}

function currentHistory(): HistoryLike | null {
  return (globalThis as { history?: HistoryLike }).history ?? null
}

/** 形状预检：恰好一个 `.`，两段都是 base64url，总长在合理区间。 */
function looksLikeToken(raw: string): boolean {
  if (raw.length < 20 || raw.length > 120) return false
  const dot = raw.indexOf('.')
  if (dot <= 0 || dot !== raw.lastIndexOf('.') || dot === raw.length - 1) return false
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(raw)
}

export function parseEmailLink(href: string): EmailLinkTicket | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  const purpose: EmailLinkPurpose | null =
    url.pathname === '/reset' ? 'reset' : url.pathname === '/signin' ? 'login' : null
  if (purpose === null) return null
  if (!url.hash.startsWith('#t=')) return null
  const token = url.hash.slice(3)
  return looksLikeToken(token) ? { purpose, token } : null
}

/** 已经消费过一次的标记。第二次调用返回 null，绝不重复兑换。 */
let consumed = false
let pending: EmailLinkTicket | null = null

/**
 * 读走地址里的令牌并把地址抹成 `/`。整个进程只生效一次。
 *
 * ★ 认不出令牌时**不碰地址栏** —— 否则它会把 `/r/K7M2P9` 这样的房间码一起抹掉，
 *   而房间码的消费者是 `takeRoomCode()`，那边就再也读不到了。
 */
export function takeEmailLink(
  href: string = currentHref(),
  history: HistoryLike | null = currentHistory(),
): EmailLinkTicket | null {
  if (consumed) return null
  const ticket = parseEmailLink(href)
  if (ticket === null) return null
  consumed = true
  pending = ticket
  clearRoomRoute(history)
  return ticket
}

/** AppRoot 读走之后暂存在这里，供大厅里的面板取用（避免一路 props 穿三层）。 */
export function pendingEmailLink(): EmailLinkTicket | null {
  return pending
}

export function clearPendingEmailLink(): void {
  pending = null
}

/** 测试用：把「只消费一次」的闸门复位。 */
export function resetEmailLinkConsumption(): void {
  consumed = false
  pending = null
}
